import React, { useEffect, useState } from 'react'
import { BsFileEarmark, BsImage, BsLink45Deg } from 'react-icons/bs'
import { buildBrandPalette } from '../../services/brandTheme'
import { getTextColorChoices } from './shared'

function BrandProfilePreview({
  profile,
  profileName,
  onProfileNameChange,
  onApply,
  onSave,
  saveDisabled = false,
}) {
  if (!profile) return null

  return (
    <div className="brand-profile-card">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <div className="small text-uppercase text-muted">Extracted profile</div>
          <strong>{profile.name}</strong>
          <div className="small text-muted">{profile.sourceLabel || profile.sourceUrl}</div>
        </div>
        <div className="brand-profile-swatches">
          {(profile.palette || []).slice(0, 8).map((color) => (
            <span
              key={`${profile.id}-${color.hex}`}
              className="brand-profile-swatch"
              title={`${color.name}: ${color.hex}`}
              style={{ backgroundColor: color.hex }}
            />
          ))}
        </div>
      </div>
      <div className="small text-muted mb-2">
        Font: {profile.fontCandidates?.[0] || 'No clear brand font found'}
      </div>
      {profile.notes?.length > 0 && (
        <div className="small text-muted mb-2">{profile.notes[0]}</div>
      )}
      <div className="brand-profile-actions">
        <input
          value={profileName}
          onChange={(event) => onProfileNameChange(event.target.value)}
          className="form-control text-field brand-profile-name-input"
          placeholder="Profile name"
        />
      </div>
      <button className="btn btn-sm btn-primary mr-2" onClick={() => onApply(profile)}>
        Apply profile
      </button>
      <button
        className="btn btn-sm btn-outline-secondary"
        disabled={saveDisabled}
        onClick={() => onSave(profile)}
      >
        Save profile
      </button>
    </div>
  )
}

function BrandPaletteButtons({ palette, onSelect, activeColor }) {
  if (!palette?.length) return null

  return (
    <div className="brand-palette-buttons">
      {palette.slice(0, 6).map((color) => {
        const hex = typeof color === 'string' ? color : color.hex
        const name = typeof color === 'string' ? color : color.name
        return (
          <button
            key={`${name || hex}-${hex}`}
            type="button"
            className={[
              'brand-palette-button',
              activeColor === hex ? 'brand-palette-button-active' : '',
            ].join(' ')}
            onClick={() => onSelect(hex)}
            title={`${name || 'Brand color'}: ${hex}`}
            style={{ backgroundColor: hex }}
          />
        )
      })}
    </div>
  )
}

function PaletteSwatchField({
  label,
  value,
  onChange,
  palette,
  onPaletteSelect,
}) {
  return (
    <div className="appearance-swatch-field">
      <label className="appearance-swatch-label">{label}</label>
      <div className="appearance-swatch-control">
        <input
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="brand-color-input appearance-swatch-input"
          aria-label={label}
        />
      </div>
      <div className="appearance-swatch-hex">{value}</div>
      <BrandPaletteButtons
        palette={palette}
        activeColor={value}
        onSelect={onPaletteSelect}
      />
    </div>
  )
}

async function importProfileFromPdfFile(file) {
  const [{ extractBrandProfileFromText }, { extractTextFromPdfFile }] =
    await Promise.all([
      import('../../services/styleGuideExtraction'),
      import('../../services/styleGuidePdf'),
    ])

  const text = await extractTextFromPdfFile(file)
  return extractBrandProfileFromText(text, {
    sourceType: 'pdf-upload',
    sourceLabel: file.name,
  })
}

async function importProfileFromImageFile(file) {
  const { extractBrandProfileFromImageFile } = await import(
    '../../services/styleGuideImage'
  )
  return extractBrandProfileFromImageFile(file)
}

async function importProfileFromUrl(url) {
  const trimmedUrl = url.trim()
  const [
    { extractBrandProfileFromPaletteUrl, extractBrandProfileFromText },
    { extractTextFromPdfUrl },
  ] = await Promise.all([
    import('../../services/styleGuideExtraction'),
    import('../../services/styleGuidePdf'),
  ])

  try {
    return extractBrandProfileFromPaletteUrl(trimmedUrl)
  } catch (error) {
    if (!/palette/i.test(error?.message || '')) {
      throw error
    }
  }

  const text = await extractTextFromPdfUrl(trimmedUrl)
  const label = trimmedUrl.split('/').pop() || trimmedUrl

  return extractBrandProfileFromText(text, {
    sourceType: 'pdf-url',
    sourceLabel: label,
    sourceUrl: trimmedUrl,
  })
}

export default function BrandThemePanel({
  theme,
  setTheme,
  savedProfiles = [],
  onApplyProfile,
  onSaveProfile,
  onDeleteProfile,
  onApplyPaletteToChart,
  onUseThemePaletteForChart,
  hasChartColorScale = false,
  hasMappedColorField = false,
  hasConfigurableColorDomain = false,
  colorDimensionLabel = null,
  canApplyPaletteToChart = false,
  paletteApplyDisabledReason = '',
  chartColorSyncMode = 'brand',
  chartPaletteDirty = false,
  onDismissPaletteUpdatePrompt,
  isMobileLayout = false,
  useAccordionLayout = isMobileLayout,
  mobileOpenSection = 'chart-palette',
  onToggleMobileSection,
}) {
  const [statusMsg, setStatusMsg] = useState('')
  const [importUrl, setImportUrl] = useState('')
  const [importingProfile, setImportingProfile] = useState(false)
  const [importError, setImportError] = useState('')
  const [extractedProfile, setExtractedProfile] = useState(null)
  const [extractedProfileName, setExtractedProfileName] = useState('')
  const [selectedProfileId, setSelectedProfileId] = useState(savedProfiles[0]?.id || '')

  const selectedProfile =
    savedProfiles.find((profile) => profile.id === selectedProfileId) || null
  const activePalette = extractedProfile?.palette?.length
    ? extractedProfile.palette
    : selectedProfile?.palette || theme.palette || []
  const generatedPalette = buildBrandPalette(theme)
  const chartPalette = activePalette.length
    ? activePalette
        .map((color) => color?.hex)
        .filter(Boolean)
    : generatedPalette
  const canApplyDirectlyToChartColors =
    canApplyPaletteToChart && activePalette.length > 0
  const chartColorSourceLabel = !hasChartColorScale
    ? 'No chart color scale'
    : chartColorSyncMode === 'manual'
      ? 'Customised'
      : chartColorSyncMode === 'brand'
        ? 'Using brand palette'
        : 'Using defaults'
  const applyButtonLabel = canApplyDirectlyToChartColors
    ? 'Use imported palette in chart'
    : 'Apply'
  const applyHelpText = !hasChartColorScale
    ? 'This chart does not expose native chart color controls, so the brand colors above will style the chart directly.'
    : !hasConfigurableColorDomain
      ? 'This chart is using one overall color right now. Use your brand colors in the chart now, or map a field to Color for per-category colors.'
      : !hasMappedColorField
        ? 'This chart uses a chart-defined dataset color domain, so the palette above can be applied directly even without a separate Color mapping field.'
      : activePalette.length === 0
        ? 'Import or choose a saved palette to apply dataset colors directly. Otherwise, the selected brand colors above will be used.'
        : paletteApplyDisabledReason || ''

  const set = (key, value) => setTheme({ ...theme, [key]: value })

  useEffect(() => {
    if (!savedProfiles.length) {
      setSelectedProfileId('')
      return
    }

    if (!savedProfiles.some((profile) => profile.id === selectedProfileId)) {
      setSelectedProfileId(savedProfiles[0].id)
    }
  }, [savedProfiles, selectedProfileId])

  useEffect(() => {
    setExtractedProfileName(extractedProfile?.name || '')
  }, [extractedProfile])

  async function handleImportFile(event) {
    const file = event.target.files?.[0]
    if (!file) return

    setImportingProfile(true)
    setImportError('')
    setExtractedProfile(null)

    try {
      const profile = await importProfileFromPdfFile(file)
      setExtractedProfile(profile)
      setStatusMsg('Palette imported')
      setTimeout(() => setStatusMsg(''), 2000)
    } catch (error) {
      setImportError(error?.message || 'Could not read that PDF style guide.')
    } finally {
      setImportingProfile(false)
      event.target.value = ''
    }
  }

  async function handleImportImage(event) {
    const file = event.target.files?.[0]
    if (!file) return

    setImportingProfile(true)
    setImportError('')
    setExtractedProfile(null)

    try {
      const profile = await importProfileFromImageFile(file)
      setExtractedProfile(profile)
      setStatusMsg('Palette imported')
      setTimeout(() => setStatusMsg(''), 2000)
    } catch (error) {
      setImportError(error?.message || 'Could not read that palette image.')
    } finally {
      setImportingProfile(false)
      event.target.value = ''
    }
  }

  async function handleImportUrl() {
    if (!importUrl.trim()) return

    setImportingProfile(true)
    setImportError('')
    setExtractedProfile(null)

    try {
      const profile = await importProfileFromUrl(importUrl)
      setExtractedProfile(profile)
      setStatusMsg('Palette imported')
      setTimeout(() => setStatusMsg(''), 2000)
    } catch (error) {
      setImportError(
        error?.message ||
          'Could not load that URL. Palette URLs can be imported directly, and PDF URLs still depend on CORS.'
      )
    } finally {
      setImportingProfile(false)
    }
  }

  function handleSaveProfile(profile) {
    const profileToSave = {
      ...profile,
      name: extractedProfileName.trim() || profile.name,
    }
    onSaveProfile(profileToSave)
    setSelectedProfileId(profileToSave.id)
    setStatusMsg('Profile saved!')
    setTimeout(() => setStatusMsg(''), 2000)
  }

  function handleApplySavedProfile() {
    const profile = savedProfiles.find((item) => item.id === selectedProfileId)
    if (!profile) return
    onApplyProfile(profile)
    setStatusMsg('Profile applied!')
    setTimeout(() => setStatusMsg(''), 2000)
  }

  function handleDeleteSavedProfile() {
    if (!selectedProfileId) return
    onDeleteProfile(selectedProfileId)
    const nextProfile = savedProfiles.find((item) => item.id !== selectedProfileId)
    setSelectedProfileId(nextProfile?.id || '')
  }

  function handleApplyPalette() {
    if (canApplyDirectlyToChartColors && activePalette.length > 0) {
      onApplyPaletteToChart?.(activePalette)
      setStatusMsg('Imported palette applied')
      setTimeout(() => setStatusMsg(''), 2000)
      return
    }

    if (chartColorSyncMode === 'brand' && !chartPaletteDirty) {
      setStatusMsg('Brand colors already in use')
      setTimeout(() => setStatusMsg(''), 2000)
      return
    }

    if (theme.primaryColor || theme.secondaryColor) {
      onUseThemePaletteForChart?.()
      setStatusMsg('Brand colors applied')
      setTimeout(() => setStatusMsg(''), 2000)
    } else {
      setStatusMsg('Choose brand colors first')
      setTimeout(() => setStatusMsg(''), 2000)
    }
  }

  function handleApplyBrandPaletteUpdate() {
    onUseThemePaletteForChart?.()
    setStatusMsg('Applied to chart')
    setTimeout(() => setStatusMsg(''), 2000)
  }

  function renderImportSection() {
    return (
      <>
        <div className="brand-profile-import mt-2">
          <div className="small text-muted mb-2">
            Upload a PDF, upload a palette image, paste a public PDF URL, or
            paste a palette URL like Color Hunt. Upload is still the most
            reliable option for PDFs.
          </div>
          <div className="appearance-import-option-grid">
            <label className="appearance-import-option">
              <BsFileEarmark aria-hidden="true" />
              <span>PDF</span>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleImportFile}
                style={{ display: 'none' }}
              />
            </label>
            <label className="appearance-import-option">
              <BsImage aria-hidden="true" />
              <span>Image</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleImportImage}
                style={{ display: 'none' }}
              />
            </label>
            <button
              type="button"
              className="appearance-import-option"
              onClick={() => {
                const field = document.querySelector('.brand-profile-url')
                field?.focus()
              }}
            >
              <BsLink45Deg aria-hidden="true" />
              <span>URL</span>
            </button>
          </div>
          <div className="d-flex flex-wrap align-items-center mb-2">
            <input
              value={importUrl}
              onChange={(event) => setImportUrl(event.target.value)}
              placeholder="Public PDF or palette URL"
              className="form-control text-field brand-profile-url mr-2"
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleImportUrl}
              disabled={importingProfile || !importUrl.trim()}
            >
              {importingProfile ? 'Reading…' : 'Import URL'}
            </button>
          </div>
          {importError && <div className="small text-danger mb-2">{importError}</div>}
          <BrandProfilePreview
            profile={extractedProfile}
            profileName={extractedProfileName}
            onProfileNameChange={setExtractedProfileName}
            onApply={onApplyProfile}
            onSave={handleSaveProfile}
          />
            {savedProfiles.length > 0 && (
              <div className="brand-profile-card mt-2">
                <div className="small text-uppercase text-muted mb-2">Saved profiles</div>
                <div className="brand-profile-saved-controls">
                  <select
                    value={selectedProfileId}
                    onChange={(event) => setSelectedProfileId(event.target.value)}
                    className="custom-select raw-select"
                  >
                    {savedProfiles.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
                  <div className="brand-profile-saved-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleApplySavedProfile}
                    disabled={!selectedProfileId}
                  >
                    Apply saved
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleDeleteSavedProfile}
                    disabled={!selectedProfileId}
                  >
                    Remove
                  </button>
                  </div>
              </div>
            </div>
          )}
        </div>
      </>
    )
  }

  function renderPaletteSection() {
    const textColorChoices = getTextColorChoices(activePalette)
    const paletteForColors = activePalette.length ? activePalette : generatedPalette
    return (
      <>
        <div className="appearance-swatch-grid">
          <PaletteSwatchField
            label="Primary"
            value={theme.primaryColor}
            onChange={(hex) => set('primaryColor', hex)}
            palette={paletteForColors}
            onPaletteSelect={(hex) => set('primaryColor', hex)}
          />
          <PaletteSwatchField
            label="Secondary"
            value={theme.secondaryColor}
            onChange={(hex) => set('secondaryColor', hex)}
            palette={paletteForColors}
            onPaletteSelect={(hex) => set('secondaryColor', hex)}
          />
          <PaletteSwatchField
            label="Background"
            value={theme.backgroundColor}
            onChange={(hex) => set('backgroundColor', hex)}
            palette={paletteForColors}
            onPaletteSelect={(hex) => set('backgroundColor', hex)}
          />
          <PaletteSwatchField
            label="Text"
            value={theme.textColor}
            onChange={(hex) => set('textColor', hex)}
            palette={textColorChoices}
            onPaletteSelect={(hex) => set('textColor', hex)}
          />
          <PaletteSwatchField
            label="Chart labels"
            value={theme.labelColor}
            onChange={(hex) => set('labelColor', hex)}
            palette={textColorChoices}
            onPaletteSelect={(hex) => set('labelColor', hex)}
          />
        </div>
        <div className="appearance-derived-palette mt-3">
          <div className="small text-uppercase text-muted mb-2">Derived palette</div>
          {renderChartPaletteSection()}
        </div>
      </>
    )
  }

  function renderChartPaletteSection() {
    return (
      <>
        <div className="appearance-derived-palette">
          <div className="small text-muted mb-2">
            These 6 colors will be applied to chart marks when you use your palette.
          </div>
          <div className="brand-profile-swatches brand-profile-swatches-left ml-0">
            {chartPalette.map((hex, index) => (
              <span
                key={`chart-palette-preview-${hex}-${index}`}
                className="brand-profile-swatch"
                title={hex}
                style={{ backgroundColor: hex }}
              />
            ))}
          </div>
        </div>

        <div className="brand-profile-inline-actions mt-3">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleApplyPalette}
          >
            {applyButtonLabel}
          </button>
          {statusMsg && <span className="text-success small">{statusMsg}</span>}
        </div>
        {applyHelpText && (
          <div className="small text-muted mt-2">
            {applyHelpText}
          </div>
        )}
      </>
    )
  }

  function renderDataColorsStatus() {
    return (
      <>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <div className="small text-uppercase text-muted">Data colors</div>
            <div className="small text-muted">
              Connect your palette to the chart’s native color controls below.
            </div>
          </div>
          <span className="appearance-status-pill">{chartColorSourceLabel}</span>
        </div>
        {activePalette.length > 0 && (
          <div className="brand-profile-swatches brand-profile-swatches-left mb-2 ml-0">
            {activePalette.slice(0, 8).map((color) => (
              <span
                key={`chart-palette-${color.name || color.hex}-${color.hex}`}
                className="brand-profile-swatch"
                title={`${color.name || 'Palette color'}: ${color.hex}`}
                style={{ backgroundColor: color.hex }}
              />
            ))}
          </div>
        )}
        {chartPaletteDirty && chartColorSyncMode === 'brand' && (
          <div className="appearance-inline-prompt mb-2">
            <span>Your palette changed — update chart colors?</span>
            <div className="brand-profile-inline-actions">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleApplyBrandPaletteUpdate}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                onClick={onDismissPaletteUpdatePrompt}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {hasChartColorScale && colorDimensionLabel && (
          <div className="small text-muted mt-2">
            This chart’s dataset colors are currently driven by <strong>{colorDimensionLabel}</strong>.
          </div>
        )}
      </>
    )
  }

  return (
    <div className="mb-3">
      {useAccordionLayout ? (
        <>
          <div className="mobile-style-accordion appearance-import-disclosure">
            <button
              type="button"
              className="mobile-style-accordion__summary"
              aria-expanded={mobileOpenSection === 'import'}
              onClick={() => onToggleMobileSection?.('import')}
            >
              <div className="mobile-style-accordion__title-row">
                <span className="accordion-section__title">Import from style guide</span>
              </div>
            </button>
            {mobileOpenSection === 'import' && (
              <div className="mobile-style-accordion__content">{renderImportSection()}</div>
            )}
          </div>
          <div className="mobile-style-accordion">
            <button
              type="button"
              className="mobile-style-accordion__summary"
              aria-expanded={mobileOpenSection === 'palette'}
              onClick={() => onToggleMobileSection?.('palette')}
            >
              <div className="mobile-style-accordion__title-row">
                <span className="accordion-section__title">Your palette</span>
                {mobileOpenSection !== 'palette' && (
                  <div className="appearance-palette-preview">
                    {[theme.primaryColor, theme.secondaryColor, theme.backgroundColor, theme.textColor].map((color, i) => (
                      <span
                        key={`palette-dot-${i}`}
                        className="appearance-palette-dot"
                        style={{ backgroundColor: color }}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                )}
              </div>
            </button>
            {mobileOpenSection === 'palette' && (
              <div className="mobile-style-accordion__content">{renderPaletteSection()}</div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="appearance-section mb-3">
            <details className="appearance-import-disclosure mb-3">
              <summary className="accordion-section__title">Import from style guide</summary>
              {renderImportSection()}
            </details>
            <div className="accordion-section__title mb-2">Your palette</div>
            {renderPaletteSection()}
          </div>

          <div className="appearance-section mb-3">
            {renderDataColorsStatus()}
          </div>
        </>
      )}
    </div>
  )
}
